import { na1 } from 'hubspot-url-utils/hublets';
import enviro from 'enviro';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useEffectEvent } from '../../hooks/useEffectEvent';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import throttle from 'hs-lodash/throttle';
import { convertToMinutes } from '../../utils/time';
import { jsx as _jsx } from "react/jsx-runtime";
const ON_TIME_PROGRESS_THROTTLE_INTERVAL = 3000;
const ACVideoPlayer = ({
  videoId,
  autoPlay,
  startTime,
  mutedByDefault,
  onError = () => {},
  onFinish = () => {},
  onLoaded = () => {},
  onLoading = () => {},
  onPause = () => {},
  onPlay = () => {},
  onReady = () => {},
  onProgress = () => {},
  onTimeUpdate: onTimeUpdateProp = () => {},
  onPlayBackRateChange = () => {},
  onTimeProgress = () => {},
  onMilestone = () => {},
  onAbandon = () => {},
  onActivation = () => {},
  playbackRate = 1,
  creatorHubId,
  creatorRegion = na1,
  duration,
  activationThreshold = 10,
  subtitleLanguage,
  enablePictureInPicture = true,
  enablePictureInPicturePersistence = true,
  playerType,
  onToggleAutoPlay = () => {},
  enableAutoPlayToggle = false,
  authContext
}) => {
  const previousTime = useRef(startTime || 0);
  const throttledTimeUpdateMinutesWatched = useMemo(() => throttle(
  // eslint-disable-next-line react-hooks/refs
  seconds => {
    const timeDifference = seconds - previousTime.current;
    previousTime.current = seconds;
    if (timeDifference > 0 &&
    // To exclude view duration events triggered by user-initiated time scrubbing via the video progress bar
    timeDifference < ON_TIME_PROGRESS_THROTTLE_INTERVAL * 2) {
      const viewDuration = convertToMinutes(timeDifference);
      onTimeProgress(viewDuration);
    }
  }, ON_TIME_PROGRESS_THROTTLE_INTERVAL, {
    leading: false,
    trailing: true
  }), [onTimeProgress]);
  const activationEventFired = useRef(false);
  const milestone25Fired = useRef(false);
  const milestone50Fired = useRef(false);
  const milestone75Fired = useRef(false);
  const hasFinished = useRef(false);
  const timeWatched = useRef(0);
  const granularPreviousTime = useRef(startTime || 0);
  const fireAbandon = useEffectEvent(() => {
    if (timeWatched.current > 0 && !hasFinished.current && duration !== undefined && duration > 0) {
      onAbandon(timeWatched.current, duration, granularPreviousTime.current);
    }
  });
  useEffect(() => {
    return () => {
      fireAbandon();
    };
  }, []);
  const handleTimeUpdate = useCallback(seconds => {
    onTimeUpdateProp(seconds);
    const timeDelta = seconds - granularPreviousTime.current;
    if (timeDelta > 0 &&
    // prevents updating if the user skips backwards
    timeDelta < 1 // prevents updating if the users skips too far forwards
    ) {
      timeWatched.current += seconds - granularPreviousTime.current;
    }
    granularPreviousTime.current = seconds;
    throttledTimeUpdateMinutesWatched(seconds);
    if (timeWatched.current > activationThreshold && !activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
    if (duration && duration > 0) {
      const ratio = timeWatched.current / duration;
      if (!milestone25Fired.current && ratio >= 0.25) {
        milestone25Fired.current = true;
        onMilestone(25);
      }
      if (!milestone50Fired.current && ratio >= 0.5) {
        milestone50Fired.current = true;
        onMilestone(50);
      }
      if (!milestone75Fired.current && ratio >= 0.75) {
        milestone75Fired.current = true;
        onMilestone(75);
      }
    }
  }, [onTimeUpdateProp, throttledTimeUpdateMinutesWatched, activationThreshold, onActivation, onMilestone, duration]);
  const handleProgressHsVideo = useCallback(e => {
    if (e && e.detail) {
      onProgress({
        currentTime: e.detail.currentTime,
        subtitleLanguage: e.detail.textLanguage
      });
    }
  }, [onProgress]);
  const handleTimeUpdateHsVideo = useCallback(e => {
    if (e && e.detail && e.detail.currentTime) {
      handleTimeUpdate(e.detail.currentTime);
    }
  }, [handleTimeUpdate]);
  const handleFinish = useCallback(() => {
    hasFinished.current = true;
    onFinish();
    if (!activationEventFired.current) {
      onActivation();
      activationEventFired.current = true;
    }
  }, [onActivation, onFinish]);
  const handleLoaded = useCallback(() => {
    onLoaded();
  }, [onLoaded]);
  const handleLoading = useCallback(() => {
    onLoading();
  }, [onLoading]);
  return /*#__PURE__*/_jsx("div", {
    "data-player-type": "HUBSPOT_VIDEO",
    "data-player-video-id": videoId,
    "data-test-id": "ac-video-player",
    children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
      authContext: authContext,
      autoPlay: autoPlay,
      currentTime: startTime !== undefined && startTime >= 0 ? startTime : undefined,
      enableAutoPlayToggle: enableAutoPlayToggle,
      enableParentHotKeys: true,
      enablePictureInPicture: enablePictureInPicture,
      enablePictureInPicturePersistence: enablePictureInPicturePersistence,
      enableVideoFrameHotKeys: true,
      env: enviro.isQa() ? 'qa' : 'prod',
      mutedByDefault: mutedByDefault,
      onError: onError,
      onFinish: handleFinish,
      onLoaded: handleLoaded,
      onLoading: handleLoading,
      onPause: onPause,
      onPlay: onPlay,
      onPlayBackRateChange: onPlayBackRateChange,
      onReady: onReady,
      onTimeUpdate: handleTimeUpdateHsVideo,
      onToggleAutoPlay: onToggleAutoPlay,
      onUpdateProgress: handleProgressHsVideo,
      playbackRate: playbackRate,
      playerType: playerType,
      portalId: creatorHubId,
      preload: "auto",
      region: creatorRegion,
      subtitleLanguage: subtitleLanguage,
      videoId: videoId,
      videoPlaySpeed: playbackRate * 100
    }, videoId)
  });
};
export default ACVideoPlayer;